import { QueryHookOptions } from '@apollo/client';
import { groupBy, uniqBy } from 'lodash';

import { getSuggestionEvidenceDocumentIds } from '@/modules/aiSuggestions/evidence';
import { AiSuggestionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { AiSuggestion_DispositiveProvisionFragment } from '../graphql/aiDP.generated';
import {
  FetchDispositiveProvisionSuggestionsV2Query,
  FetchDispositiveProvisionSuggestionsV2QueryVariables,
  useFetchDispositiveProvisionSuggestionsV2Query,
} from '../graphql/aiDPV2.generated';

type DPSuggestionsQueryOptions = QueryHookOptions<
  FetchDispositiveProvisionSuggestionsV2Query,
  FetchDispositiveProvisionSuggestionsV2QueryVariables
>;

interface UseFetchDPSuggestionsOptions
  extends Omit<DPSuggestionsQueryOptions, 'variables'> {
  /** The entity or testamentary entity ID to fetch suggestions for */
  targetId: string | undefined;
  /** Whether targetId is an entity */
  isEntity: boolean;
  /** Whether targetId is a testamentary entity */
  isTestamentaryEntity: boolean;
}

/**
 * Fetches DP_RECIPIENT_V2 suggestions through their explicit entity or
 * testamentary entity relationship.
 */
export function useFetchDPSuggestions({
  targetId,
  isEntity,
  isTestamentaryEntity,
  skip,
  ...options
}: UseFetchDPSuggestionsOptions) {
  return useFetchDispositiveProvisionSuggestionsV2Query({
    skip: skip || !targetId,
    variables: {
      targetId: targetId!,
      isEntity,
      isTestamentaryEntity,
    },
    ...options,
  });
}

export interface ProcessedDPSuggestionsData {
  suggestions: AiSuggestion_DispositiveProvisionFragment[];
  suggestionsByKind: Partial<
    Record<AiSuggestionKind, AiSuggestion_DispositiveProvisionFragment[]>
  >;
  suggestionDocumentIds: string[];
}

/**
 * Processes V2 query data, extracting DP suggestions.
 * Document IDs come from file paragraphs linked to each suggestion's evidence.
 */
export function processDPSuggestionsDataV2(
  data: FetchDispositiveProvisionSuggestionsV2Query
): ProcessedDPSuggestionsData {
  const suggestions = [
    ...getNodes(data.entityDPSuggestions),
    ...getNodes(data.testamentaryEntityDPSuggestions),
  ] as AiSuggestion_DispositiveProvisionFragment[];

  const suggestionsByKind = groupBy(suggestions, (s) => s.kind) as Partial<
    Record<AiSuggestionKind, AiSuggestion_DispositiveProvisionFragment[]>
  >;

  const suggestionDocumentIds = uniqBy(
    suggestions.flatMap((s) => getSuggestionEvidenceDocumentIds(s.evidence)),
    (id) => id
  );

  return {
    suggestions,
    suggestionsByKind,
    suggestionDocumentIds,
  };
}
